/**
 * ============================================================
 * MENSAJES DE VALIDACIÓN – SALVIR
 * ============================================================
 *
 * Este módulo centraliza los mensajes de validación
 * mostrados al usuario mediante Toastify.
 *
 * Cada mensaje corresponde directamente a una función
 * de validación ubicada en:
 *
 * utils/validaciones/identidad.js
 *
 * Convención utilizada:
 *
 * validarX  → msgX
 *
 * Esto permite mantener consistencia entre:
 * - lógica de validación
 * - mensajes de interfaz
 *
 */

import { toast } from "react-toastify";

/**
 * validarLongitudCURP
 * ------------------------------------------------------------
 * Mensaje cuando la CURP no tiene 18 caracteres.
 */
export const msgLongitudCURP = () => {
  toast.error("La CURP debe contener exactamente 18 caracteres.");
};

export const msgDigitoVerificador = () => {
   toast.error("El último carácter de la CURP debe ser un número (dígito verificador).");
 };


/**
 * validarFormatoCURP
 * ------------------------------------------------------------
 * Mensaje cuando la CURP no cumple con el formato RENAPO.
 */
export const msgCurpPos1 = () => {
   toast.error("Posición 1 de la CURP: la inicial del apellido paterno no coincide.");
 };
 export const msgCurpPos2 = () => {
   toast.error("Posición 2 de la CURP: la vocal interna del apellido paterno no coincide.");
 };
 export const msgCurpPos3 = () => {
   toast.error("Posición 3 de la CURP: la inicial del apellido materno no coincide.");
 };
 export const msgCurpPos4 = () => {
   toast.error("Posición 4 de la CURP: la inicial del nombre no coincide.");
 };
 export const msgCurpPos5 = () => {
   toast.error("Posición 5 de la CURP: el primer dígito del año de nacimiento no coincide.");
 };
 export const msgCurpPos6 = () => {
   toast.error("Posición 6 de la CURP: el segundo dígito del año de nacimiento no coincide.");
 };
 export const msgCurpPos7 = () => {
   toast.error("Posición 7 de la CURP: el primer dígito del mes de nacimiento no coincide.");
 };
 export const msgCurpPos8 = () => {
   toast.error("Posición 8 de la CURP: el segundo dígito del mes de nacimiento no coincide.");
 };
 export const msgCurpPos9 = () => {
   toast.error("Posición 9 de la CURP: el primer dígito del día de nacimiento no coincide.");
 };
 export const msgCurpPos10 = () => {
   toast.error("Posición 10 de la CURP: el segundo dígito del día de nacimiento no coincide.");
 };
 export const msgCurpPos11 = () => {
   toast.error("Posición 11 de la CURP: el sexo no coincide con el registrado.");
 };
 export const msgCurpPos12 = () => {
   toast.error("Posición 12 de la CURP: el primer caracter de la entidad de nacimiento no coincide.");
 };
 export const msgCurpPos13 = () => {
   toast.error("Posición 13 de la CURP: el segundo caracter de la entidad de nacimiento no coincide.");
 };
 export const msgCurpPos14 = () => {
   toast.error("Posición 14 de la CURP: la consonante interna del apellido paterno no coincide.");
 };
 export const msgCurpPos15 = () => {
   toast.error("Posición 15 de la CURP: la consonante interna del apellido materno no coincide.");
 };
 export const msgCurpPos16 = () => {
   toast.error("Posición 16 de la CURP: la consonante interna del nombre no coincide.");
 };

/**
 * validarFechaCURP
 * ------------------------------------------------------------
 * Mensaje cuando la fecha dentro de la CURP no es válida.
 */
export const msgFechaCURP = () => {
  toast.error("La fecha contenida en la CURP no es válida.");
};


/**
 * validarEdadRango
 * ------------------------------------------------------------
 * Mensaje cuando la edad calculada desde la CURP
 * no se encuentra dentro del rango permitido.
 *
 * NOM-024 exige rango 18-90 para prestadores.
 */
export const msgEdadRango = () => {
  toast.error("La edad del prestador debe estar entre 18 y 90 años.");
};

/**
  * validarEdadPaciente
  * ------------------------------------------------------------
  * Mensaje cuando la edad del paciente excede el máximo permitido.
  *
  * Regla Salvir:
  * Edad máxima permitida para registro de paciente: 120 años.
  */
export const msgEdadPaciente = () => {
 toast.warning(
    "El paciente ya tiene 121 años. El sistema solo permite registrar atenciones realizadas hasta los 120 años de edad. Si la atención ocurrió en una fecha anterior, dentro de las últimas 48 horas y cuando el paciente aún tenía 120 años, podrá registrarla utilizando la fecha real en que se brindó la atención."
  );
};

export const msgFechaNacimientoNoFutura = () => {
  toast.error("La fecha de nacimiento no puede ser posterior a la fecha actual.");
};

export const msgFechaNacimientoMinima = () => {
   toast.error("La fecha de nacimiento no puede ser anterior al año 1900.");
 };

 export const msgFechaNacimientoRequerida = () => {
  toast.error("La fecha de nacimiento es obligatoria para validar la CURP.");
};

export const msgApellidoMaternoRequeridoCURP = () => {
  toast.error("El apellido materno es obligatorio para validar la CURP.");
};

export const msgEntidadNacimientoRequeridaCURP = () => {
  toast.error("La entidad de nacimiento es obligatoria para validar la CURP.");
};

export const msgPaisNacimientoRequeridoCURP = () => {
  toast.error("El país de nacimiento es obligatorio para validar la CURP.");
};

export const msgEdadEstimadaNoMenorDeUnAnio = () => {
  toast.error("La fecha estimada ingresada corresponde a una edad de 1 año o más. Si se estima que el paciente es mayor de 1 año, desmarque la casilla de es menor de 1 año.");
};

export const msgEdadEstimadaNoMayorOIgualAUnAnio = () => {
  toast.error("La fecha estimada ingresada corresponde a una edad menor de 1 año. Marque la casilla 'Es menor de 1 año' para capturar mes y año si se estima es menor a 1 año, si se estima que es mayor cambie el año. ");
};

export const msgMesNacimientoRequerido = () => {
  toast.error("Por favor capture el mes aproximado de nacimiento.");
};

/**
 * coincideCurpConNombre
 * ------------------------------------------------------------
 * Mensaje cuando la CURP no coincide con
 * nombre y apellidos registrados.
 */
export const msgCoincidenciaCURPNombre = () => {
  toast.error("La CURP no coincide con el nombre y apellidos registrados.");
};

/**
+ * validarCurpContraFechaNacimiento
+ * ------------------------------------------------------------
+ * Mensaje cuando la fecha de nacimiento registrada
+ * no coincide con la fecha contenida dentro de la CURP.
+ */
export const msgFechaNacimientoCURP = () => {
  toast.error("La fecha de nacimiento no coincide con la registrada en la CURP.");
};

export const msgSigloCURPFechaNacimiento = () => {
  toast.error(
    "Posición 17 de la CURP: el carácter ingresado no es válido. Debe ser un número o una letra permitida, según el año de nacimiento."
  );
};


/**
 * msgEntidadNacimientoCURP
 * ------------------------------------------------------------
 * Muestra un mensaje cuando la entidad de nacimiento registrada
 * no coincide con la entidad codificada dentro de la CURP.
 */

export const msgEntidadNacimientoCURP = () => {
  toast.error(
    "La entidad de nacimiento no coincide con la registrada en la CURP."
  );
};

/**
 * msgSexoCURP
 * ------------------------------------------------------------
 * Muestra un mensaje cuando el sexo seleccionado
 * no coincide con el sexo codificado dentro de la CURP.
 */

export const msgSexoCURP = () => {
  toast.error(
    "El sexo seleccionado no coincide con el registrado en la CURP."
  );
};

export const msgCurpPacientePrestador = () => {
   toast.error(
     "La CURP del paciente no puede ser igual a la del prestador de servicios.",
   );
 };

/**
 * Validación de nombre o apellidos
 * ------------------------------------------------------------
 * Se usa cuando el nombre contiene caracteres inválidos.
 */
export const msgNombreInvalido = () => {
  toast.error(
    `Nombre o apellido inválido. Verifique:
    -Entre 2 y 50 caracteres
    -Solo letras (se permiten acentos y Ñ)
    -Separadores permitidos: ( ), (-), (.), (,), (/), ('), (¨)
    -No usar separadores consecutivos
`
  );
};

export const msgNombreLongitud = () => {
   toast.error("El nombre debe tener entre 2 y 50 caracteres.");
 };
export const msgNombreEspacios = () => {
  toast.error("El nombre no puede contener espacios consecutivos.");
};
export const msgNombreCaracteres = () => {
  toast.error("El nombre solo puede contener letras, Ñ y los caracteres: - , . / ' ¨ Sin caracteres especiales consecutivos.");
};

export const msgApellidoPaternoLongitud = () => {
  toast.error("El apellido paterno debe tener entre 2 y 50 caracteres.");
};

export const msgApellidoPaternoEspacios = () => {
  toast.error("El apellido paterno no puede contener espacios consecutivos.");
};

export const msgApellidoPaternoCaracteres = () => {
  toast.error("El apellido paterno solo puede contener letras, Ñ y los caracteres: - , . / ' ¨ Sin caracteres especiales consecutivos.");
};

export const msgApellidoMaternoLongitud = () => {
  toast.error("El apellido materno debe tener entre 2 y 50 caracteres.");
};

export const msgApellidoMaternoEspacios = () => {
  toast.error("El apellido materno no puede contener espacios consecutivos.");
};

export const msgApellidoMaternoCaracteres = () => {
  toast.error("El apellido materno solo puede contener letras, Ñ y los caracteres: - , . / ' ¨ Sin caracteres especiales consecutivos.");
};

// ============================================================
// MENSAJES - SIGNOS VITALES
// ============================================================


// ------------------------------------------------------------
// Presión sistólica fuera de rango
// ------------------------------------------------------------
export const msgPresionSistolicaFueraRango = () => {
  toast.error("La presión sistólica debe estar entre 50 y 300 mmHg. Si se desconoce, usa el switch 'Se desconoce'.");
};


// ------------------------------------------------------------
// Presión diastólica fuera de rango
// ------------------------------------------------------------
export const msgPresionDiastolicaFueraRango = () => {
  toast.error("La presión diastólica debe estar entre 20 y 200 mmHg. Si se desconoce, usa el switch 'Se desconoce'.");
};

export const msgDiastolicaMayorASistolica = () => {
  toast.error("La presión diastólica no puede ser mayor que la sistólica");
};
// ------------------------------------------------------------
// Frecuencia cardiaca
// ------------------------------------------------------------

export const msgFrecuenciaCardiacaFueraRango = () => {
  toast.error("La frecuencia cardiaca debe estar entre 40 y 220 lpm. Si se desconoce, usa el switch 'Se desconoce'.");
};
// ------------------------------------------------------------
//Frecuencia respiratoria
// ------------------------------------------------------------

export const msgFrecuenciaRespiratoriaFueraRango = () => {
  toast.error("La frecuencia respiratoria debe estar entre 10 y 99 rpm");
};

// ------------------------------------------------------------
// Saturación de oxígeno
// ------------------------------------------------------------

export const msgSaturacionOxigenoFueraRango = () => {
  toast.error("La saturación de oxígeno debe estar entre 1 y 100 %");
};

// ------------------------------------------------------------
// Temperatura
// ------------------------------------------------------------

export const msgTemperaturaFueraRango = () => {
  toast.error("La temperatura debe estar entre 30 y 44 °C");
};

// ------------------------------------------------------------
// Circunferencia de cintura
// ------------------------------------------------------------
export const msgCircunferenciaCinturaFueraRango = () => {
  toast.error("La circunferencia de cintura debe estar entre 20 y 300 cm.");
};

// ------------------------------------------------------------
// Glucemia
// ------------------------------------------------------------
export const msgGlucemiaFueraRango = () => {
  toast.error("La glucemia debe estar entre 20 y 999 mg/dl.");
};

// ------------------------------------------------------------
// Fecha de consulta NOM-024
// ------------------------------------------------------------
export const msgFechaConsultaFuturo = () => {
  toast.error("La fecha de consulta no puede ser a futuro.");
};

export const msgFechaConsulta48h = () => {
  toast.error("La fecha de consulta no puede ser mayor a 48 horas en el pasado.");
};

export const msgFechaConsultaAntesNacimiento = () => {
  toast.error("La fecha de consulta no puede ser anterior a la fecha de nacimiento del paciente.");
};

//talla 

export const msgAlturaFueraRango = () => {
  toast.error("La talla debe estar entre 0.30 y 2.20 m. Si se desconoce, usa el switch 'Se desconoce'.");
};

// ------------------------------------------------------------
// Peso
// ------------------------------------------------------------
export const msgPesoFueraRango = () => {
  toast.error("El peso debe estar entre 1 y 400 kg. Si se desconoce, usa el switch 'Se desconoce'.");
};